import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as LogoMark } from '../assets/images/logo-mark.svg';

const API_BASE = process.env.REACT_APP_API_URL || 'https://backend-production-9457.up.railway.app';

function WaitlistForm({ btnLabel = 'Join the waitlist', note, style }) {
  const [email, setEmail]   = useState('');
  const [done, setDone]     = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(`${API_BASE}/portal/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
    } catch (_) {}
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'waitlist_signup' });
    setDone(true);
    setLoading(false);
  }

  if (done) {
    return (
      <div className="waitlist-done">
        <span style={{ color: '#00FFFF', fontWeight: 700, marginRight: 8 }}>✓</span>
        You're on the list — we'll be in touch.
      </div>
    );
  }

  return (
    <div style={style}>
      <form className="waitlist-form" onSubmit={handleSubmit}>
        <input
          className="waitlist-input"
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          disabled={loading}
        />
        <button className="waitlist-btn" type="submit" disabled={loading}
          style={{ opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Joining...' : btnLabel}
        </button>
      </form>
      {note && <p className="waitlist-note">{note}</p>}
    </div>
  );
}

export default function Waitlist() {
  const particlesRef = useRef(null);

  useEffect(() => {
    const container = particlesRef.current;
    if (!container) return;
    for (let i = 0; i < 28; i++) {
      const p      = document.createElement('div');
      const size   = Math.random() * 2.5 + 0.8;
      const dur    = Math.random() * 12 + 8;
      const delay  = Math.random() * -20;
      const drift  = (Math.random() - 0.5) * 80;
      const magma  = Math.random() < 0.12;
      p.className  = 'particle';
      p.style.cssText = `
        width:${size}px; height:${size}px;
        left:${Math.random() * 100}%; bottom:${Math.random() * 20}%;
        background:${magma ? '#FF5722' : '#00FFFF'};
        animation-duration:${dur}s; animation-delay:${delay}s;
        --drift:${drift}px; opacity:0;
      `;
      container.appendChild(p);
    }
  }, []);

  return (
    <div className="scq-page">
      <style>{`
        :root {
          --void:     #030A3C;
          --void-2:   #060D4A;
          --void-3:   #0A1260;
          --void-4:   #0D1570;
          --cyan:     #00FFFF;
          --cyan-dim: rgba(0,255,255,0.10);
          --cyan-mid: rgba(0,255,255,0.30);
          --magma:    #FF5722;
          --magma-dim:rgba(255,87,34,0.12);
          --white:    #F8F9FA;
          --muted:    rgba(248,249,250,0.75);
          --muted-2:  rgba(248,249,250,0.72);
          --border:   rgba(0,255,255,0.08);
          --border-2: rgba(0,255,255,0.15);
        }
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { overflow-x: hidden; }

        .scq-page {
          background: var(--void);
          color: var(--white);
          font-family: 'Inter', sans-serif;
          font-size: 16px;
          line-height: 1.5;
          -webkit-font-smoothing: antialiased;
          min-height: 100vh;
        }

        /* ── NAV ── */
        .scq-nav {
          position: fixed; top: 0; left: 0; right: 0; z-index: 100;
          display: flex; align-items: center; justify-content: space-between;
          padding: 20px 48px;
          background: rgba(3,10,60,0.85);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid var(--border);
        }
        .scq-nav-logo {
          font-family: 'Satoshi','Inter',sans-serif; font-weight: 700;
          font-size: 18px; letter-spacing: -0.02em; color: var(--white);
          display: flex; align-items: center; gap: 8px; flex-shrink: 0;
        }
        .scq-nav-links {
          display: flex; gap: 32px; list-style: none;
        }
        .scq-nav-links a {
          font-size: 14px; color: var(--muted); text-decoration: none;
          transition: color 0.2s;
        }
        .scq-nav-links a:hover { color: var(--white); }
        .scq-nav-right { display: flex; align-items: center; gap: 16px; flex-shrink: 0; }
        .scq-nav-signin {
          font-size: 14px; color: var(--muted); text-decoration: none;
          transition: color 0.2s;
        }
        .scq-nav-signin:hover { color: var(--white); }
        .scq-nav-cta {
          font-family: 'Satoshi','Inter',sans-serif; font-weight: 700;
          font-size: 13px; letter-spacing: -0.01em;
          padding: 10px 20px; border-radius: 8px;
          background: var(--magma); color: #fff; text-decoration: none;
          transition: opacity 0.2s, transform 0.15s;
        }
        .scq-nav-cta:hover { opacity: 0.88; transform: translateY(-1px); }

        /* ── HERO ── */
        .scq-hero {
          min-height: 100vh;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          text-align: center;
          padding: 140px 24px 100px;
          position: relative; overflow: hidden;
        }
        .scq-hero::before {
          content: '';
          position: absolute; inset: 0;
          background:
            radial-gradient(ellipse 70% 55% at 50% -5%, rgba(0,255,255,0.09) 0%, transparent 65%),
            radial-gradient(ellipse 40% 35% at 85% 85%, rgba(255,87,34,0.07) 0%, transparent 60%),
            radial-gradient(ellipse 45% 50% at 8% 65%, rgba(0,255,255,0.04) 0%, transparent 55%);
          pointer-events: none;
        }
        .scq-particles { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
        .particle {
          position: absolute; border-radius: 50%;
          animation: scqFloat linear infinite;
        }
        @keyframes scqFloat {
          0%   { transform: translateY(0) translateX(0) scale(1); opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 0.6; }
          100% { transform: translateY(-280px) translateX(var(--drift)) scale(0.5); opacity: 0; }
        }

        /* Floating insight cards */
        .insight-card {
          position: absolute;
          background: rgba(6,13,74,0.85);
          border: 1px solid var(--border-2);
          border-radius: 12px; padding: 16px 18px;
          text-align: left; width: 230px;
          backdrop-filter: blur(12px);
          animation: cardFloat ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes cardFloat {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-12px); }
        }
        .insight-card-left  { left: 4%;  top: 30%; animation-duration: 6s; }
        .insight-card-right { right: 4%; top: 38%; animation-duration: 7.5s; animation-delay: -2s; }
        .insight-card-right2{ right: 3%; top: 62%; animation-duration: 5.5s; animation-delay: -1s; width: 210px; }
        .ic-label {
          font-size: 9px; font-weight: 600; letter-spacing: 0.1em;
          text-transform: uppercase; color: var(--cyan); margin-bottom: 8px;
          display: flex; align-items: center; gap: 5px;
        }
        .ic-label::before {
          content: ''; width: 5px; height: 5px; border-radius: 50%;
          background: var(--cyan); flex-shrink: 0;
        }
        .ic-theme {
          font-family: 'Satoshi','Inter',sans-serif; font-weight: 700;
          font-size: 12px; letter-spacing: -0.01em; color: var(--white); margin-bottom: 6px;
        }
        .ic-quote { font-size: 11px; color: var(--muted); line-height: 1.5; font-style: italic; }
        .ic-meta {
          margin-top: 10px; font-size: 9px; color: rgba(0,255,255,0.4);
          display: flex; align-items: center; gap: 8px;
        }
        .ic-confidence { display: flex; gap: 2px; align-items: center; }
        .ic-bar { height: 3px; border-radius: 2px; background: var(--cyan); }
        .ic-magma { font-size: 9px; color: var(--magma); font-weight: 600; }

        /* Hero content */
        .hero-content { position: relative; z-index: 2; }
        .badge {
          display: inline-flex; align-items: center; gap: 8px;
          font-family: 'Satoshi','Inter',sans-serif; font-weight: 700;
          font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase;
          color: var(--cyan); border: 1px solid var(--cyan-mid);
          background: var(--cyan-dim); padding: 6px 16px; border-radius: 100px; margin-bottom: 32px;
        }
        .badge-dot {
          width: 6px; height: 6px; border-radius: 50%; background: var(--cyan);
          animation: scqPulse 2s infinite;
        }
        @keyframes scqPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.35; transform: scale(0.75); }
        }
        .hero-h1 {
          font-family: 'Satoshi','Inter',sans-serif;
          font-size: clamp(42px, 6.5vw, 82px);
          font-weight: 700; line-height: 1.04; letter-spacing: -0.02em;
          color: var(--white); max-width: 820px; margin: 0 auto 24px;
        }
        .hero-h1 .accent { color: var(--cyan); }
        .hero-sub {
          font-size: clamp(16px, 2vw, 19px); color: var(--muted);
          max-width: 500px; margin: 0 auto 48px; line-height: 1.7;
        }

        /* Waitlist form */
        .waitlist-form {
          display: flex; gap: 12px; justify-content: center;
          flex-wrap: wrap; margin-bottom: 14px;
        }
        .waitlist-input {
          font-family: 'Inter',sans-serif; font-size: 15px;
          padding: 14px 20px; border-radius: 10px;
          border: 1px solid var(--border-2);
          background: rgba(255,255,255,0.05); color: var(--white);
          width: 300px; outline: none;
          transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
        }
        .waitlist-input::placeholder { color: var(--muted-2); }
        .waitlist-input:focus {
          border-color: var(--cyan-mid);
          background: rgba(0,255,255,0.04);
          box-shadow: 0 0 0 3px rgba(0,255,255,0.06);
        }
        .waitlist-btn {
          font-family: 'Satoshi','Inter',sans-serif; font-weight: 700;
          font-size: 15px; letter-spacing: -0.01em;
          padding: 14px 28px; border-radius: 10px;
          background: var(--magma); color: #fff; border: none; cursor: pointer;
          transition: opacity 0.2s, transform 0.15s;
        }
        .waitlist-btn:hover { opacity: 0.88; transform: translateY(-1px); }
        .waitlist-note { font-size: 12px; color: var(--muted-2); }
        .waitlist-done {
          padding: 14px 28px; border-radius: 10px;
          background: rgba(42,139,122,0.12); border: 1px solid rgba(42,139,122,0.25);
          color: #2A8B7A; font-family: 'Satoshi','Inter',sans-serif;
          font-weight: 700; font-size: 15px; text-align: center; display: inline-block;
        }

        /* Social proof */
        .social-proof-row {
          display: flex; align-items: center; justify-content: center;
          gap: 20px; margin-top: 52px; padding-top: 36px;
          border-top: 1px solid var(--border); flex-wrap: wrap;
        }
        .avatar-stack { display: flex; }
        .avatar {
          width: 34px; height: 34px; border-radius: 50%;
          border: 2px solid var(--void); margin-left: -10px;
          background: var(--void-3); display: flex; align-items: center;
          justify-content: center; font-family: 'Satoshi','Inter',sans-serif;
          font-size: 11px; font-weight: 700; color: var(--cyan); flex-shrink: 0;
        }
        .avatar:first-child { margin-left: 0; }
        .avatar-overflow { font-size: 9px; color: var(--muted); background: rgba(0,255,255,0.07); }
        .social-proof-text { text-align: left; }
        .social-proof-text strong {
          font-family: 'Satoshi','Inter',sans-serif; font-weight: 700;
          font-size: 14px; color: var(--white); display: block;
        }
        .social-proof-text span { font-size: 12px; color: var(--muted); }
        .social-divider { width: 1px; height: 32px; background: var(--border-2); }
        .star-rating { display: flex; flex-direction: column; align-items: center; gap: 3px; }
        .stars { display: flex; gap: 2px; }
        .star { color: #FFB400; font-size: 13px; }
        .rating-text { font-size: 11px; color: var(--muted); }

        /* ── STATS STRIP ── */
        .stats-strip { background: var(--void-2); border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); }
        .stats-inner {
          max-width: 1100px; margin: 0 auto;
          display: grid; grid-template-columns: repeat(4,1fr);
          gap: 1px; background: var(--border);
        }
        .stat { background: var(--void-2); padding: 32px; text-align: center; }
        .stat-number {
          font-family: 'Satoshi','Inter',sans-serif; font-weight: 700;
          font-size: 38px; letter-spacing: -0.03em; color: var(--white);
          line-height: 1; margin-bottom: 6px;
        }
        .stat-number span { color: var(--cyan); }
        .stat-label { font-size: 13px; color: var(--muted); }

        /* ── LOGOS STRIP ── */
        .logos-strip { padding: 52px 24px; text-align: center; max-width: 1100px; margin: 0 auto; }
        .logos-label {
          font-size: 12px; font-weight: 500; letter-spacing: 0.1em;
          text-transform: uppercase; color: var(--muted-2); margin-bottom: 28px;
        }
        .logos-row { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
        .logo-pill {
          display: flex; align-items: center; gap: 8px;
          padding: 10px 20px; border-radius: 100px;
          border: 1px solid var(--border-2); background: var(--void-2);
          font-size: 13px; font-weight: 500; color: var(--muted);
          transition: border-color 0.2s, color 0.2s;
        }
        .logo-pill:hover { border-color: var(--cyan-mid); color: var(--white); }
        .logo-pill-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }

        /* ── HOW IT WORKS ── */
        .how-section { padding: 100px 24px; }
        .section-wrap { max-width: 1100px; margin: 0 auto; }
        .section-eyebrow {
          font-size: 11px; font-weight: 500; letter-spacing: 0.12em;
          text-transform: uppercase; color: var(--cyan); margin-bottom: 16px;
        }
        .section-heading {
          font-family: 'Satoshi','Inter',sans-serif;
          font-size: clamp(28px, 4vw, 44px); font-weight: 700;
          line-height: 1.1; letter-spacing: -0.02em; margin-bottom: 56px; max-width: 560px;
        }
        .pipeline { display: grid; grid-template-columns: repeat(4,1fr); position: relative; }
        .pipeline::before {
          content: '';
          position: absolute; top: 54px;
          left: calc(12.5% + 18px); right: calc(12.5% + 18px);
          height: 1px;
          background: linear-gradient(90deg, var(--cyan-mid), var(--cyan-dim) 50%, transparent);
          z-index: 0;
        }
        .pipeline-step {
          background: var(--void-2); border: 1px solid var(--border);
          border-radius: 16px; padding: 32px 24px 28px; margin: 0 8px;
          position: relative; z-index: 1;
          transition: border-color 0.25s, box-shadow 0.25s;
        }
        .pipeline-step:hover {
          border-color: var(--border-2);
          box-shadow: 0 0 40px rgba(0,255,255,0.05);
        }
        .step-node {
          width: 36px; height: 36px; border-radius: 50%;
          background: var(--void-3); border: 1.5px solid var(--border-2);
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 20px; position: relative;
        }
        .step-node svg { width: 16px; height: 16px; stroke: var(--cyan); fill: none; stroke-width: 1.5; }
        .step-node::after {
          content: ''; position: absolute; inset: -4px;
          border-radius: 50%; border: 1px solid rgba(0,255,255,0.08);
        }
        .step-number {
          font-family: 'Satoshi','Inter',sans-serif; font-size: 10px; font-weight: 700;
          letter-spacing: 0.1em; color: rgba(0,255,255,0.5); margin-bottom: 12px;
        }
        .step-title {
          font-family: 'Satoshi','Inter',sans-serif; font-size: 15px; font-weight: 700;
          letter-spacing: -0.02em; color: var(--white); margin-bottom: 8px;
        }
        .step-desc { font-size: 13px; color: var(--muted); line-height: 1.6; }

        /* ── FEATURES ── */
        .features-wrap {
          background: var(--void-2);
          border-top: 1px solid var(--border); border-bottom: 1px solid var(--border);
        }
        .features-inner { max-width: 1100px; margin: 0 auto; padding: 80px 24px; }
        .features-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; }
        .feature {
          padding: 40px 36px; border-right: 1px solid var(--border);
          transition: background 0.2s;
        }
        .feature:hover { background: rgba(0,255,255,0.02); }
        .feature:last-child { border-right: none; }
        .feature-icon {
          width: 40px; height: 40px; border-radius: 10px;
          background: var(--cyan-dim);
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 20px;
        }
        .feature-icon svg { width: 20px; height: 20px; stroke: var(--cyan); fill: none; stroke-width: 1.5; }
        .feature h3 { font-family: 'Satoshi','Inter',sans-serif; font-size: 17px; font-weight: 700; color: var(--white); margin-bottom: 10px; letter-spacing: -0.01em; }
        .feature p  { font-size: 13px; color: var(--muted); line-height: 1.65; }
        .feature-tag {
          display: inline-block; margin-top: 16px; font-size: 10px; font-weight: 600;
          padding: 4px 10px; border-radius: 100px;
          background: var(--magma-dim); color: var(--magma);
          border: 1px solid rgba(255,87,34,0.2);
        }

        /* ── TESTIMONIALS ── */
        .testimonials-section { padding: 100px 24px; }
        .testimonials-grid {
          display: grid; grid-template-columns: 1fr 1fr 1fr;
          gap: 20px; margin-top: 56px;
        }
        .testimonial {
          background: var(--void-2); border: 1px solid var(--border);
          border-radius: 20px; padding: 32px; position: relative;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .testimonial:hover {
          border-color: var(--border-2);
          box-shadow: 0 8px 40px rgba(0,255,255,0.05);
        }
        .testimonial.featured {
          border-color: rgba(0,255,255,0.2);
          background: linear-gradient(135deg, var(--void-3) 0%, var(--void-2) 100%);
        }
        .testimonial.featured::before {
          content: 'Most cited';
          position: absolute; top: -1px; right: 24px;
          font-family: 'Satoshi','Inter',sans-serif; font-size: 10px; font-weight: 700;
          letter-spacing: 0.05em; text-transform: uppercase;
          color: var(--void); background: var(--cyan);
          padding: 4px 10px; border-radius: 0 0 8px 8px;
        }
        .quote-mark {
          font-family: Georgia,serif; font-size: 48px; line-height: 0.8;
          color: var(--cyan); opacity: 0.3; margin-bottom: 12px;
        }
        .testimonial-text { font-size: 14px; line-height: 1.7; color: var(--white); opacity: 0.85; margin-bottom: 24px; }
        .testimonial-text strong { color: var(--cyan); font-weight: 500; }
        .testimonial-author {
          display: flex; align-items: center; gap: 12px;
          padding-top: 20px; border-top: 1px solid var(--border);
        }
        .author-avatar {
          width: 36px; height: 36px; border-radius: 50%;
          background: var(--void-4); border: 1px solid var(--border-2);
          display: flex; align-items: center; justify-content: center;
          font-family: 'Satoshi','Inter',sans-serif; font-size: 11px;
          font-weight: 700; color: var(--cyan); flex-shrink: 0;
        }
        .author-name {
          font-family: 'Satoshi','Inter',sans-serif; font-size: 13px;
          font-weight: 700; color: var(--white); letter-spacing: -0.01em;
        }
        .author-role { font-size: 11px; color: var(--muted); margin-top: 1px; }

        /* ── BOTTOM CTA ── */
        .cta-wrap { padding: 0 24px 100px; max-width: 1100px; margin: 0 auto; }
        .cta-section {
          border-radius: 24px;
          background: linear-gradient(135deg, #0A1260 0%, #0A0E4A 100%);
          border: 1px solid var(--border-2);
          padding: 80px 48px; text-align: center; position: relative; overflow: hidden;
        }
        .cta-section::before {
          content: '';
          position: absolute; top: -80px; left: 50%;
          transform: translateX(-50%);
          width: 500px; height: 250px;
          background: radial-gradient(ellipse, rgba(0,255,255,0.08) 0%, transparent 70%);
          pointer-events: none;
        }
        .cta-section h2 {
          font-family: 'Satoshi','Inter',sans-serif;
          font-size: clamp(28px, 4vw, 44px); font-weight: 700;
          letter-spacing: -0.02em; margin-bottom: 16px; position: relative;
        }
        .cta-section p {
          font-size: 16px; color: var(--muted); margin-bottom: 40px;
          max-width: 440px; margin-left: auto; margin-right: auto; position: relative;
        }
        .cta-social-proof {
          display: flex; align-items: center; justify-content: center;
          gap: 28px; margin-top: 32px; padding-top: 28px;
          border-top: 1px solid var(--border); flex-wrap: wrap;
        }
        .cta-sp-item {
          font-size: 12px; color: var(--muted);
          display: flex; align-items: center; gap: 6px;
        }
        .cta-sp-item::before { content: '✓'; color: var(--cyan); font-weight: 700; font-size: 11px; }

        /* ── FOOTER ── */
        .scq-footer {
          border-top: 1px solid var(--border);
          padding: 32px 48px;
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 16px;
        }
        .footer-logo {
          font-family: 'Satoshi','Inter',sans-serif; font-weight: 700;
          font-size: 15px; color: var(--muted); letter-spacing: -0.02em;
        }
        .footer-tagline { font-size: 11px; color: var(--muted-2); margin-top: 2px; }
        .footer-links { display: flex; gap: 24px; list-style: none; flex-wrap: wrap; align-items: center; }
        .footer-links a {
          font-size: 12px; color: var(--muted-2); text-decoration: none; transition: color 0.2s;
        }
        .footer-links a:hover { color: var(--muted); }
        .footer-links button {
          font-size: 12px; color: var(--muted-2); background: none; border: none;
          cursor: pointer; padding: 0; text-decoration: underline;
          text-decoration-color: rgba(248,249,250,0.2); transition: color 0.2s;
        }
        .footer-links button:hover { color: var(--muted); }
      `}</style>

      {/* NAV */}
      <nav className="scq-nav">
        <div className="scq-nav-logo">
          <LogoMark width="22" height="22" style={{ flexShrink: 0 }} />
          SenseCritiq
        </div>
        <ul className="scq-nav-links">
          <li><Link to="/how-it-works">How it works</Link></li>
          <li><Link to="/features">Features</Link></li>
          <li><Link to="/researchers">For researchers</Link></li>
          <li><Link to="/pricing">Pricing</Link></li>
        </ul>
        <div className="scq-nav-right">
          <Link to="/sign-in" className="scq-nav-signin">Sign in</Link>
          <a href="#waitlist" className="scq-nav-cta">Join waitlist</a>
        </div>
      </nav>

      {/* HERO */}
      <section className="scq-hero">
        <div ref={particlesRef} className="scq-particles" />

        <div className="insight-card insight-card-left">
          <div className="ic-label">Theme identified</div>
          <div className="ic-theme">Onboarding friction</div>
          <div className="ic-quote">"I didn't know where to start — the first screen was just overwhelming."</div>
          <div className="ic-meta">
            <span>P4 · 03:22</span>
            <div className="ic-confidence">
              <div className="ic-bar" style={{ width: 28, opacity: 0.9 }} />
              <div className="ic-bar" style={{ width: 18, opacity: 0.5 }} />
              <div className="ic-bar" style={{ width: 8,  opacity: 0.2 }} />
            </div>
            <span className="ic-magma">● High</span>
          </div>
        </div>

        <div className="insight-card insight-card-right">
          <div className="ic-label">Key finding</div>
          <div className="ic-theme">Trust signals missing</div>
          <div className="ic-quote">"I wanted to see proof it worked before I gave it my data."</div>
          <div className="ic-meta">
            <span>P2 · 11:47</span>
            <span className="ic-magma">● Critical</span>
          </div>
        </div>

        <div className="insight-card insight-card-right2">
          <div className="ic-label">Synthesis complete</div>
          <div className="ic-theme">3 themes · 14 quotes</div>
          <div className="ic-quote">Report ready — 47 min processing time.</div>
        </div>

        <div className="hero-content">
          <div className="badge">
            <span className="badge-dot" />
            Early access — 2026
          </div>

          <h1 className="hero-h1">
            Map user emotion.<br />
            Reach <span className="accent">certainty.</span>
          </h1>

          <p className="hero-sub">
            SenseCritiq synthesises your UX research automatically — turning hours of raw interviews into structured themes, verbatim evidence, and shareable reports.
          </p>

          <div id="waitlist" style={{ scrollMarginTop: 120 }}>
            <WaitlistForm note="No spam. Early access invitation when we launch." />
          </div>

          <div className="social-proof-row">
            <div className="avatar-stack">
              {['JK','MR','AS','LP','TW'].map(init => (
                <div key={init} className="avatar">{init}</div>
              ))}
              <div className="avatar avatar-overflow">+60</div>
            </div>
            <div className="social-proof-text">
              <strong>65 researchers on the waitlist</strong>
              <span>From Spotify, Intercom, Wise &amp; more</span>
            </div>
            <div className="social-divider" />
            <div className="star-rating">
              <div className="stars">
                {'★★★★★'.split('').map((_, i) => <span key={i} className="star">★</span>)}
              </div>
              <div className="rating-text">4.9 · 23 beta reviews</div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <div className="stats-strip">
        <div className="stats-inner">
          {[
            { num: ['1',',','240','+'], label: 'Interviews processed in beta' },
            { num: ['47','m'],          label: 'Avg. synthesis time per session' },
            { num: ['2','.','1','d'],   label: 'Avg. time saved per study' },
            { num: ['0','%'],           label: 'Findings without a source citation' },
          ].map(({ num, label }) => (
            <div key={label} className="stat">
              <div className="stat-number">
                {num.map((part, i) =>
                  /^[0-9]+$/.test(part)
                    ? <span key={i} style={{ color: 'var(--white)' }}>{part}</span>
                    : <span key={i}>{part}</span>
                )}
              </div>
              <div className="stat-label">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* LOGOS STRIP */}
      <div style={{ background: 'var(--void)' }}>
        <div className="logos-strip">
          <div className="logos-label">Researchers from these organisations on the waitlist</div>
          <div className="logos-row">
            {[
              { name: 'Spotify',     color: '#1ED760' },
              { name: 'Intercom',    color: '#2563EB' },
              { name: 'Wise',        color: '#00B9FF' },
              { name: 'Hotjar',      color: '#FF6B00' },
              { name: 'Maze',        color: '#7C3AED' },
              { name: 'UserTesting', color: '#0EA5E9' },
              { name: 'Figma',       color: '#F59E0B' },
            ].map(({ name, color }) => (
              <div key={name} className="logo-pill">
                <div className="logo-pill-dot" style={{ background: color }} />
                {name}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="how-section">
        <div className="section-wrap">
          <div className="section-eyebrow">How it works</div>
          <h2 className="section-heading">From raw transcript to<br />strategic insight in minutes.</h2>
          <div className="pipeline">
            {[
              {
                n: '01', title: 'Upload',
                icon: <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>,
                desc: 'Drop in audio, video, or a transcript. Any format. We handle the rest.',
              },
              {
                n: '02', title: 'Transcribe & Filter',
                icon: <path d="M12 2a10 10 0 100 20 10 10 0 000-20zM2 12h4M18 12h4M12 2v4M12 18v4"/>,
                desc: 'AssemblyAI transcription with speaker diarisation. Noise removed, signal kept.',
              },
              {
                n: '03', title: 'AI Synthesis',
                icon: <><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M2 12h4M18 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></>,
                desc: 'Claude extracts themes, key findings, and verbatim quotes — each one source-cited.',
              },
              {
                n: '04', title: 'Report & Search',
                icon: <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8"/>,
                desc: 'Share a Markdown or PDF report. Query your entire research history in natural language.',
              },
            ].map(({ n, title, icon, desc }) => (
              <div key={n} className="pipeline-step">
                <div className="step-number">{n}</div>
                <div className="step-node">
                  <svg viewBox="0 0 24 24">{icon}</svg>
                </div>
                <div className="step-title">{title}</div>
                <div className="step-desc">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <div id="features" className="features-wrap">
        <div className="features-inner">
          <div className="section-eyebrow">Insight infrastructure</div>
          <h2 className="section-heading">Everything researchers actually need.</h2>
          <div className="features-grid">
            {[
              {
                icon: <path d="M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>,
                title: 'Every finding has a source',
                desc:  'No hallucinated insights. Every theme and finding is pinned to a verbatim quote, speaker, and timestamp. Researcher trust depends on accuracy.',
                tag:   'Evidence-led',
              },
              {
                icon: <><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></>,
                title: 'Queryable research memory',
                desc:  'Search across every session you\'ve ever run. Ask "what did participants say about onboarding?" and get ranked answers with sources — instantly.',
                tag:   'Semantic search',
              },
              {
                icon: <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7a4 4 0 100 8 4 4 0 000-8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>,
                title: 'Built for your workflow',
                desc:  'Use it via the MCP server in Claude Desktop, through the web portal, or pipe findings directly into Notion. Research where you already work.',
                tag:   'MCP-native',
              },
            ].map(({ icon, title, desc, tag }) => (
              <div key={title} className="feature">
                <div className="feature-icon">
                  <svg viewBox="0 0 24 24">{icon}</svg>
                </div>
                <h3>{title}</h3>
                <p>{desc}</p>
                <span className="feature-tag">{tag}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="testimonials-section">
        <div className="section-wrap">
          <div className="section-eyebrow">What researchers say</div>
          <h2 className="section-heading">From the people who actually do this work.</h2>
          <div className="testimonials-grid">
            {[
              {
                initials: 'SR', name: 'Sophie R.',  role: 'Senior UX Researcher · Fintech, London',
                featured: true,
                text: <>The synthesis phase used to take me <strong>two full days</strong> per study. SenseCritiq does it before my debrief. And every quote actually links back to the source — that's the part I didn't expect to matter as much as it does.</>,
              },
              {
                initials: 'MK', name: 'Marcus K.',  role: 'ResearchOps Lead · SaaS, Berlin',
                featured: false,
                text: <>I was sceptical about AI synthesis — I've been burned by tools that just <strong>make things up</strong>. SenseCritiq is different. Every theme cites the exact moment in the transcript. My stakeholders trust it.</>,
              },
              {
                initials: 'AL', name: 'Anika L.',   role: 'UX Research Manager · E-commerce, Amsterdam',
                featured: false,
                text: <>We ran six interviews in a week. Normally that's <strong>two weeks of synthesis</strong> work. With SenseCritiq we had a shareable report the same afternoon. It's changed how we scope research projects entirely.</>,
              },
            ].map(({ initials, name, role, featured, text }) => (
              <div key={name} className={`testimonial${featured ? ' featured' : ''}`}>
                <div className="quote-mark">"</div>
                <p className="testimonial-text">{text}</p>
                <div className="testimonial-author">
                  <div className="author-avatar">{initials}</div>
                  <div>
                    <div className="author-name">{name}</div>
                    <div className="author-role">{role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <div className="cta-wrap">
        <div className="cta-section">
          <h2>Ready to reach certainty?</h2>
          <p>Join 65 researchers on the waitlist. Early access invitations go out in Q2 2026.</p>
          <WaitlistForm btnLabel="Secure your spot" style={{ position: 'relative' }} />
          <div className="cta-social-proof">
            {['No credit card required', 'Free tier on launch', 'Every finding source-cited'].map(item => (
              <span key={item} className="cta-sp-item">{item}</span>
            ))}
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="scq-footer">
        <div>
          <div className="footer-logo">SenseCritiq</div>
          <div className="footer-tagline">Map User Emotion. Reach Certainty.</div>
        </div>
        <ul className="footer-links">
          {[['Privacy', '/privacy'], ['Terms', '/terms'], ['Cookies', '/cookies'], ['Contact', 'mailto:privacy@sensecritiq.com']].map(([label, href]) => (
            <li key={label}><a href={href}>{label}</a></li>
          ))}
          <li>
            <button onClick={() => { localStorage.removeItem('scq_cookie_consent'); window.location.reload(); }}>
              Cookie settings
            </button>
          </li>
        </ul>
      </footer>
    </div>
  );
}
